import { honoraryInYear } from '../utils/honoraryMembership';
import HonoraryMembershipBadge from './HonoraryMembershipBadge';
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import MemberDetail from './MemberDetail';
import ConfirmationModal from './ConfirmationModal';
import AttributeLabel from './AttributeLabel';
import { isBoard, isTrainer, hasAdminRights } from '../utils/roleUtils';
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  UserCircleIcon,
  UserGroupIcon,
  CakeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  FunnelIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  AcademicCapIcon,
  MegaphoneIcon,
  XMarkIcon,
  MapPinIcon,
  ShieldCheckIcon,
  CloudArrowUpIcon,
  CurrencyEuroIcon,
  TicketIcon,
  ClockIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import MembersCSVImporter from './MembersCSVImporter';
import AuditHistoryModal from './AuditHistoryModal';
import MemberExcelExportModal from './MemberExcelExportModal';
import RoleManagementModal from './RoleManagementModal';
import MemberAvatar from './MemberAvatar';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { StatCard, StatGroup } from './StatCard';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import PaymentNoticeModal, { PaymentNoticeSummary } from './PaymentNoticeModal';

// Type-safe wrapper for FaDog icon
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface Member {
  kuendigung_am?: string | null;
  austritt_am?: string | null;
  ehrenmitglied_ab?: string | null;
  ehrenmitglied_bis?: string | null;
  mg_nr: string;
  mitglied_nr: string;
  name: string;
  vorname: string;
  email: string;
  tel_nummer: string;
  strasse: string;
  plz: string;
  ort: string;
  geb_am: string;
  eintritt: string;
  vorstand: number;
  trainer: number;
  active: number;
  aktiv?: number; // For compatibility with MemberDetail modal
  geloescht?: number;
  geloescht_am?: string;
  last_login?: string;
  foto_path?: string;
  foto_path_updated?: string;
  gender?: string;
  datenschutzerklaerung?: number;
  verzehrliste?: number;
  roles?: Array<{
    id: number;
    name: string;
    description: string;
    priority: number;
    created_at: string;
  }>;
  primary_role?: {
    id: number;
    name: string;
    description: string;
    priority: number;
    created_at: string;
  };
}

interface DogStatus {
  hasDogs: boolean;
  dogsPaid: boolean;
  count: number;
  dogs?: Array<{
    name: string;
    id?: string;
    valid_insurance?: boolean;
    valid_tollwut?: boolean;
  }>;
}

interface MemberPayment {
  contribution_exempt?: boolean;
  amount: number;
  last_payment_date: string | null;
  possible_member_type: string;
  membership_contributions: any;
}

/**
 * Aktives Mitglied (kein reiner Gast), das für das aktuelle Jahr keine
 * Beitragszahlung hat (kein Eintrag oder Betrag 0). Nutzt dieselbe „bezahlt"-
 * Logik wie die Anzeige (payment.amount > 0).
 */
const isUnpaidCurrentYear = (m: Member, payments: Record<string, MemberPayment>): boolean => {
  if (honoraryInYear(m, new Date().getFullYear())) return false;
  if (m.geloescht === 1) return false;
  if (m.active !== 1) return false;
  const isMember = m.roles?.some((role: any) => role.name !== 'guest');
  if (!isMember) return false;
  const payment = payments[String(m.mg_nr)];
  return !payment || !(payment.amount > 0);
};

const noticeLabel = (type: PaymentNoticeSummary['notice_type']) => ({
  reminder: 'Erinnert',
  dunning: 'Gemahnt',
  final_dunning: 'Letztmalig gemahnt',
}[type]);

const noticeTone = (type: PaymentNoticeSummary['notice_type']) => type === 'final_dunning'
  ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/25 dark:text-red-300'
  : type === 'dunning'
    ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-300'
    : 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/25 dark:text-sky-300';

const contributionStatus = (payment: MemberPayment | undefined, selectedYear: number, noticeSummary?: PaymentNoticeSummary, member?: Member) => {
  if ((member && honoraryInYear(member, selectedYear)) || payment?.contribution_exempt) return {
    label: 'Beitragsfrei', chipClass: 'inline-flex items-center rounded-full bg-info-100 px-2.5 py-0.5 text-xs font-semibold text-info-700 dark:bg-info-900/30 dark:text-info-300',
    amount: null, amountClass: '',
  };
  const currentYear = new Date().getFullYear();

  if (payment && payment.amount > 0) {
    return {
      label: 'Bezahlt',
      chipClass: 'inline-flex items-center rounded-full bg-success-100 px-2.5 py-0.5 text-xs font-semibold text-success-700 dark:bg-success-900/30 dark:text-success-300',
      amount: payment.amount,
      amountClass: 'text-xs font-medium text-success-600 dark:text-success-400',
    };
  }

  const isOverdue = selectedYear < currentYear || noticeSummary?.notice_type === 'final_dunning';

  if (isOverdue) {
    return {
      label: 'Überfällig',
      chipClass: 'inline-flex items-center rounded-full bg-error-100 px-2.5 py-0.5 text-xs font-semibold text-error-700 dark:bg-error-900/30 dark:text-error-300',
      amount: null,
      amountClass: 'text-xs font-medium text-gray-500 dark:text-gray-400',
    };
  }

  return {
    label: 'Offen',
    chipClass: 'inline-flex items-center rounded-full bg-warning-100 px-2.5 py-0.5 text-xs font-semibold text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
    amount: null,
    amountClass: 'text-xs font-medium text-gray-500 dark:text-gray-400',
  };
};

const Members: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  // Stabile Reihenfolge nach angezeigtem Namen (Vorname Nachname), unabhängig von der API-Reihenfolge
  const sortedMembers = useMemo(
    () => [...filteredMembers].sort((a, b) =>
      `${a.vorname ?? ''} ${a.name ?? ''}`.localeCompare(`${b.vorname ?? ''} ${b.name ?? ''}`, 'de', { sensitivity: 'base' })
    ),
    [filteredMembers]
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dogsStatus, setDogsStatus] = useState<Record<string, DogStatus>>({});
  const [memberPayments, setMemberPayments] = useState<Record<string, MemberPayment>>({});
  const [paymentNoticeSummaries, setPaymentNoticeSummaries] = useState<Record<string, PaymentNoticeSummary>>({});
  const [paymentNoticeMember, setPaymentNoticeMember] = useState<Member | null>(null);
  const [guestCards, setGuestCards] = useState<Record<string, { count: number; remaining: number }>>({});
  const [profileImages, setProfileImages] = useState<Record<string, string>>({});
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [filterType, setFilterType] = useState<'all' | 'active' | 'inactive' | 'board' | 'trainer' | 'members_only' | 'guests_only' | 'unpaid_current_year' | 'deleted' | 'cancelled'>('all');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([new Date().getFullYear()]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
    onCancel: () => {}
  });
  const [showNewMemberModal, setShowNewMemberModal] = useState(false);
  const [newMemberData, setNewMemberData] = useState({
    vorname: '',
    name: '',
    email: '',
    tel_nummer: '',
    strasse: '',
    plz: '',
    ort: '',
    geb_am: '',
    gender: '',
    eintritt: '',
    active: 1,
    role_ids: [1] // Default: Guest role (id: 5)
  });
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [showCSVImporter, setShowCSVImporter] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showExcelExportModal, setShowExcelExportModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasAccess = hasAdminRights(user);

  const currentYear = new Date().getFullYear();
  const isPastYear = selectedYear < currentYear;

  // Memoized filter counts for performance
  const filterCounts = useMemo(() => ({
    all: members.length,
    members: members.filter(m => m.roles?.some(role => role.name === 'member')).length,
    guests: members.filter(m => m.roles?.length === 1 && m.roles[0].name === 'guest').length,
    inactive: members.filter(m => m.active === 0).length,
    active: members.filter(m => m.active === 1).length,
    deleted: members.filter(m => m.geloescht === 1).length,
    cancelled: members.filter(m => m.austritt_am && m.geloescht !== 1).length,
    unpaidCurrentYear: members.filter(m => isUnpaidCurrentYear(m, memberPayments)).length,
  }), [members, memberPayments]);

  // Auto-focus search input when expanded
  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded]);

  // Close search on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchExpanded) {
        setSearchExpanded(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [searchExpanded]);

  // Fetch available roles
  const fetchAvailableRoles = async () => {
    try {
      const response = await apiClient.get('/api/roles/available');
      if (response.data.success && Array.isArray(response.data.roles)) {
        setAvailableRoles(response.data.roles);
      }
    } catch (error) {
      console.error('Error fetching available roles:', error);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchAvailableRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  const handleCreateMember = async () => {
    try {
      const response = await apiClient.post('/api/members', newMemberData);
      if (response.data.success) {
        setShowNewMemberModal(false);
        setNewMemberData({
          vorname: '',
          name: '',
          email: '',
          tel_nummer: '',
          strasse: '',
          plz: '',
          ort: '',
          geb_am: '',
          gender: '',
          eintritt: '',
          active: 1,
          role_ids: [1] // Reset to default Guest role
        });
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Neues Mitglied wurde erfolgreich hinzugefügt!',
          variant: 'single',
          onConfirm: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
          },
          onCancel: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
          }
        });
        fetchMembers(); // Refresh member list
      }
    } catch (error: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Fehler beim Hinzufügen des Mitglieds',
        variant: 'single',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        },
        onCancel: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      });
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const response = await apiClient.get('/api/members/available-years');
      if (response.data.success) {
        setAvailableYears(response.data.years);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching available years:', err);
      }
    }
  };

  // Jahres-unabhängige Daten einmalig laden
  useEffect(() => {
    if (hasAccess) {
      fetchAvailableYears();
      fetchDogsStatus();
      fetchGuestCards();
    }
  }, [hasAccess]);

  // Mitglieder + Zahlungen bei Jahres-/Anzeige-Wechsel neu laden
  useEffect(() => {
    if (hasAccess) {
      fetchMembers();
      fetchMemberPayments();
      fetchPaymentNoticeSummaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, selectedYear, showDeleted]);

  // Bei Wechsel in ein vergangenes Jahr den Gelöscht-Filter zurücksetzen,
  // sobald er im aktuellen Jahr nicht mehr sinnvoll ist.
  useEffect(() => {
    if (!isPastYear && filterType === 'deleted') setFilterType('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPastYear]);

  useEffect(() => {
    filterMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterType, members, memberPayments]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const url = selectedYear < currentYear
        ? `/api/members?year=${selectedYear}`
        : showDeleted
          ? '/api/members?includeDeleted=1'
          : '/api/members';
      const response = await apiClient.get(url);
      const data = response.data;
      setMembers(data);
      setFilteredMembers(data);

      // Fetch profile images for each member
      data.forEach((member: Member) => {
        if (member.foto_path && member.foto_path_updated) {
          fetchProfileImage(member.mg_nr, member.foto_path_updated);
        }
      });
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching members:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDogsStatus = async () => {
    try {
      const response = await apiClient.get('/api/members/all_dogs_status');
      const data = response.data;
      if (data.success) {
        const statusMap: Record<string, DogStatus> = {};
        data.data.members.forEach((member: any) => {
          const hasDogs = member.dogs.length > 0;
          const dogsPaid = hasDogs ? member.dogs.every((dog: any) => dog.valid_insurance && dog.valid_tollwut) : false;
          const count = member.dogs.length;
          const dogData = {
            hasDogs,
            dogsPaid,
            count,
            dogs: member.dogs.map((dog: any) => ({
              name: dog.hundename || 'Unbekannt',
              id: dog.dog_id || dog.id,
              valid_insurance: dog.valid_insurance,
              valid_tollwut: dog.valid_tollwut
            }))
          };
          // Map only by mg_nr
          statusMap[String(member.member_nr)] = dogData;
        });
        setDogsStatus(statusMap);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching dogs status:', err);
      }
    }
  };

  const fetchMemberPayments = async () => {
    try {
      const response = await apiClient.get(`/api/members/payments?currentYear=${selectedYear}`);
      const data = response.data;
      const paymentsMap: Record<string, MemberPayment> = {};

      data.forEach((member: any) => {
        const currentYearPayment = member.payments.find((p: any) => p.year === selectedYear);
        if (currentYearPayment) {
          // Use member_nr as string for consistent mapping
          paymentsMap[String(member.member_nr)] = {
            contribution_exempt: !!currentYearPayment.contribution_exempt,
            amount: currentYearPayment.amount || 0,
            last_payment_date: currentYearPayment.last_payment_date || null,
            possible_member_type: currentYearPayment.possible_member_type || 'unbekannt',
            membership_contributions: currentYearPayment.membership_contributions || {}
          };
        } else {
          // Use member_nr as string for consistent mapping
          paymentsMap[String(member.member_nr)] = {
            amount: 0,
            last_payment_date: null,
            possible_member_type: 'keine_zahlung',
            membership_contributions: {}
          };
        }
      });
      
      setMemberPayments(paymentsMap);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching payments:', err);
      }
    }
  };

  const fetchPaymentNoticeSummaries = async () => {
    try {
      const response = await apiClient.get(`/api/members/payment-notices/summary?year=${selectedYear}`);
      const summaryMap: Record<string, PaymentNoticeSummary> = {};
      (response.data.notices || []).forEach((notice: PaymentNoticeSummary) => {
        summaryMap[String(notice.member_id)] = {
          ...notice,
          notice_count: Number(notice.notice_count || 0),
        };
      });
      setPaymentNoticeSummaries(summaryMap);
    } catch (err: any) {
      if (err.response?.status !== 401) console.error('Error fetching payment notice summaries:', err);
    }
  };

  const fetchGuestCards = async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/guests-with-cards');
      if (response.data.success) {
        const cardsMap: Record<string, { count: number; remaining: number }> = {};
        response.data.guests.forEach((guest: any) => {
          cardsMap[String(guest.mg_nr)] = {
            count: guest.cards?.length || 0,
            remaining: guest.cards?.reduce((sum: number, card: any) => sum + (card.remaining_entries || 0), 0) || 0
          };
        });
        setGuestCards(cardsMap);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching guest cards:', err);
      }
    }
  };

  const fetchProfileImage = async (mg_nr: string, foto_path_updated?: string) => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) return;

      // Use foto_path_updated as timestamp if available, otherwise use current time
      const timestamp = foto_path_updated || new Date().getTime();
      const imageUrl = `${BASE_URL}/api/members/user-image-url/${mg_nr}?size=S&token=${token}&t=${timestamp}`;
      setProfileImages(prev => ({ ...prev, [mg_nr]: imageUrl }));
    } catch (err) {
      console.error(`Error fetching profile image for ${mg_nr}:`, err);
    }
  };

  const filterMembers = () => {
    let filtered = [...members];

    // Apply search filter - erweitert um PLZ, Straße, Ort, Eintrittsdatum
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(member => {
        // Name und Vorname
        if (member.name?.toLowerCase().includes(searchLower)) return true;
        if (member.vorname?.toLowerCase().includes(searchLower)) return true;
        
        // Kontaktdaten
        if (member.email?.toLowerCase().includes(searchLower)) return true;
        if (member.mitglied_nr?.toLowerCase().includes(searchLower)) return true;
        
        // Adresse
        if (member.plz?.toLowerCase().includes(searchLower)) return true;
        if (member.strasse?.toLowerCase().includes(searchLower)) return true;
        if (member.ort?.toLowerCase().includes(searchLower)) return true;
        
        // Eintrittsdatum (Format: YYYY-MM-DD oder DD.MM.YYYY für Suche)
        if (member.eintritt) {
          const eintrittFormatted = member.eintritt.replace(/-/g, '.');
          if (eintrittFormatted.includes(searchTerm) || member.eintritt.includes(searchTerm)) return true;
        }
        
        return false;
      });
    }

    // Apply type filter
    switch (filterType) {
      case 'active':
        filtered = filtered.filter(m => m.active === 1);
        break;
      case 'inactive':
        filtered = filtered.filter(m => m.active === 0);
        break;
      case 'board':
        filtered = filtered.filter(m => m.roles?.some(role => role.name === 'board'));
        break;
      case 'trainer':
        filtered = filtered.filter(m => m.roles?.some(role => role.name === 'trainer'));
        break;
      case 'members_only':
        // Nur Mitglieder (keine Gäste) - basierend auf Rollen
        filtered = filtered.filter(m => {
          if (!m.roles || m.roles.length === 0) return false;
          // Prüfe ob mindestens eine Rolle NICHT 'guest' ist
          return m.roles.some((role: any) => role.name !== 'guest');
        });
        break;
      case 'guests_only':
        // Nur Gäste - basierend auf Rollen
        filtered = filtered.filter(m => {
          if (!m.roles || m.roles.length === 0) return false;
          // Prüfe ob NUR die 'guest' Rolle vorhanden ist
          return m.roles.length === 1 && m.roles[0].name === 'guest';
        });
        break;
      case 'unpaid_current_year':
        // Aktive Mitglieder ohne Beitragszahlung im gewählten Jahr
        filtered = filtered.filter(m => isUnpaidCurrentYear(m, memberPayments));
        break;
      case 'deleted':
        // Im gewählten (vergangenen) Jahr gelöschte Mitglieder
        filtered = filtered.filter(m => m.geloescht === 1);
        break;
      case 'cancelled':
        filtered = filtered.filter(m => m.austritt_am && m.geloescht !== 1);
        break;
    }

    setFilteredMembers(filtered);
  };

  const handleViewDetails = (member: Member) => {
    setSelectedMember(member);
    setShowDetailsModal(true);
  };

  const handleMemberSave = async (updatedMember: Member) => {
    try {
      const response = await apiClient.put(`/api/members/${updatedMember.mg_nr}`, updatedMember);
      if (response.data.success) {
        const savedMember = response.data.member || updatedMember;
        setSelectedMember(savedMember);
        await fetchMembers();
        await fetchMemberPayments();
        
        // MemberDetail meldet das Ergebnis nach dem anschließenden Rollenspeichern.
        return { success: true };
      }

      return { success: false, error: response.data.error || response.data.message || 'Die Mitgliederdaten konnten nicht aktualisiert werden. Bitte erneut versuchen.' };
    } catch (error: any) {
      console.error('Error updating member:', error);

      return { success: false, error: error.response?.data?.error || error.response?.data?.message || error.message || 'Die Mitgliederdaten konnten nicht aktualisiert werden. Bitte erneut versuchen.' };
    }
  };

  const handleMemberDelete = async (memberId: string) => {
    try {
      // Delete member via API
      const response = await apiClient.delete(`/api/members/${memberId}`);

      if (response.data.success) {
        // Update local state - remove deleted member
        setMembers(prevMembers =>
          prevMembers.filter(m => m.mg_nr !== memberId)
        );

        // Close the detail modal
        setShowDetailsModal(false);
        setSelectedMember(null);

        // Show success feedback
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Mitglied wurde erfolgreich gelöscht.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      } else {
        // Show error feedback
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Das Mitglied konnte nicht gelöscht werden.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      }
    } catch (error: any) {
      console.error('Error deleting member:', error);

      // Show error feedback
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: `Fehler beim Löschen: ${error.message}`,
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel: () => {}
      });
    }
  };

  // Löschung zurücknehmen (Mitglied wiederherstellen) – mit Bestätigung
  const handleMemberRestore = (member: Member) => {
    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: 'Mitglied wiederherstellen?',
      message: `${member.vorname} ${member.name} wird wieder als aktives Mitglied geführt.${member.austritt_am ? ' Die hinterlegte Kündigung und das Mitgliedschaftsende werden aufgehoben.' : ''}`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          const response = await apiClient.post(`/api/members/${member.mg_nr}/restore`);
          if (response.data.success) {
            setShowDetailsModal(false);
            setSelectedMember(null);
            await fetchMembers();
            await fetchMemberPayments();
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Wiederhergestellt',
              message: `${member.vorname} ${member.name} wurde wiederhergestellt.`,
              variant: 'single',
              onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
              onCancel: () => {}
            });
          }
        } catch (error: any) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: `Wiederherstellung fehlgeschlagen: ${error?.response?.data?.error || error.message}`,
            variant: 'single',
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
            onCancel: () => {}
          });
        }
      },
      onCancel: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
    });
  };

  const getMemberTypeLabel = (memberType: string) => {
    const types: Record<string, string> = {
      'erwachsen': 'Erwachsen',
      'adults': 'Erwachsen',
      'vollzahler': 'Vollzahler',
      'partner': 'Partner',
      'jugend': 'Jugend',
      'youth': 'Jugend',
      'unbekannt': 'Unbekannt',
      'keine_zahlung': 'Keine Zahlung'
    };
    return types[memberType] || memberType;
  };

  const getMemberTypeIcon = (type: string, size: 'small' | 'normal' = 'normal') => {
    const className = size === 'small' ? 'h-3 w-3' : 'h-4 w-4';
    switch(type?.toLowerCase()) {
      case 'erwachsen':
      case 'adults':
      case 'vollzahler':
        return <UserIcon className={className} />;
      case 'partner':
        return <UserGroupIcon className={className} />;
      case 'jugend':
      case 'youth':
        return <AcademicCapIcon className={className} />;
      default:
        return null;
    }
  };

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Zugriff nur für Vorstand und Trainer
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Mobile Header */}
      <header className="md:hidden border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
              Mitgliederverwaltung
            </p>
            <p className="mt-0.5 text-lg font-bold text-gray-900 dark:text-white">
              {filterCounts.members} Mitglieder
            </p>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchExpanded(!searchExpanded)}
              className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
                searchExpanded
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
              aria-label="Mitglieder suchen"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setShowNewMemberModal(true)}
              className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-success-600 px-3.5 text-sm font-semibold text-white transition-colors hover:bg-success-700 active:bg-success-800"
            >
              <UserPlusIcon className="h-5 w-5" />
              Neu
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            type="button"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className={`inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors ${
              filterType !== 'all'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            <FunnelIcon className="h-4 w-4" />
            Filter
          </button>
          <button
            type="button"
            onClick={() => setShowAuditModal(true)}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg bg-gray-100 px-3 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300"
          >
            <ClockIcon className="h-4 w-4" />
            Verlauf
          </button>
          {isBoard(user) && (
            <button
              type="button"
              onClick={() => setShowRoleModal(true)}
              className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg bg-gray-100 px-3 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              <ShieldCheckIcon className="h-4 w-4" />
              Rollen
            </button>
          )}
        </div>

        {searchExpanded && (
          <div className="mt-3 flex h-11 items-center gap-2.5 rounded-xl bg-gray-100 px-3.5 dark:bg-gray-700">
            <MagnifyingGlassIcon className="h-[18px] w-[18px] flex-shrink-0 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Name, E-Mail, PLZ oder Ort"
              className="w-full border-none bg-transparent text-sm text-gray-900 outline-none placeholder-gray-400 dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
                className="rounded-full p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-600"
                aria-label="Suche löschen"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        )}
      </header>

      {/* Desktop Header */}
      <header className="app-page-header hidden md:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <UserGroupIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Mitglieder</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{members.length} Mitglieder insgesamt</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Suchfeld - 280px breit */}
            <div className="flex items-center gap-2.5 bg-gray-100 dark:bg-gray-700 rounded-xl px-4 w-[280px] h-11">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-gray-400 flex-shrink-0" />
              <input
                type="text"
                placeholder="Mitglied suchen..."
                className="bg-transparent border-none outline-none text-sm w-full text-gray-900 dark:text-white placeholder-gray-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {/* Audit History Button */}
            <button
              onClick={() => setShowAuditModal(true)}
              className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
              title="Änderungsverlauf"
            >
              <ClockIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
            {/* Filter Button */}
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
              title="Filter"
            >
              <FunnelIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
            {/* Rollenverwaltung Button - nur Vorstand */}
            {isBoard(user) && (
              <button
                onClick={() => setShowRoleModal(true)}
                className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
                title="Rollenverwaltung"
              >
                <ShieldCheckIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            )}
            {/* CSV Import Button */}
            <button
              onClick={() => setShowCSVImporter(true)}
              className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
              title="CSV-Import"
            >
              <CloudArrowUpIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
            {/* Excel Export Button */}
            <button
              onClick={() => setShowExcelExportModal(true)}
              className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
              title="Excel-Export"
            >
              <ArrowDownTrayIcon className="w-5 h-5 text-success-600" />
            </button>
            {/* Primary Button */}
            <button
              onClick={() => setShowNewMemberModal(true)}
              className="flex items-center gap-2 py-3 px-5 bg-success-600 hover:bg-success-700 active:bg-success-800 rounded-xl text-sm font-semibold text-white transition-colors"
            >
              <UserPlusIcon className="w-[18px] h-[18px]" />
              Hinzufügen
            </button>
          </div>
        </div>
      </header>

      {/* Content Area - kein Padding auf Desktop, damit Cards/Tabelle volle Breite haben */}
      <div className="px-4 md:px-0">
        {/* Zusammenfassende Kennzahlen */}
        <StatGroup columns={4} mobileColumns={4} className="mb-4 md:mb-6">
          <StatCard
            icon={ChartBarIcon}
            tone="neutral"
            label="Gesamt"
            value={members.length}
          />
          <StatCard
            icon={UserCircleIcon}
            tone="neutral"
            label="Mitglieder"
            value={members.filter(m => m.roles?.some(role => role.name === 'member')).length}
          />
          <StatCard
            icon={UserPlusIcon}
            tone="neutral"
            label="Gäste"
            value={members.filter(m => m.roles?.some(role => role.name === 'guest')).length}
          />
          <StatCard
            icon={CheckCircleIcon}
            tone="success"
            valueTone="success"
            label="Aktiv"
            value={members.filter(m => m.active === 1).length}
          />
        </StatGroup>

        {/* Jahres-Selektor + Filter Chips - Wrapping on mobile with icons */}
        <div className="flex flex-wrap items-center gap-1.5 md:gap-3 mb-3 md:mb-4">
          {/* Jahres-Selektor */}
          <div className="relative inline-flex items-center">
            <CalendarDaysIcon className={`pointer-events-none absolute left-2.5 h-3.5 w-3.5 md:h-4 md:w-4 ${isPastYear ? 'text-amber-500' : 'text-gray-400'}`} />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              title="Jahr wählen"
              className={`appearance-none rounded-full border py-1.5 md:py-2 pl-7 md:pl-8 pr-7 md:pr-8 text-[11px] md:text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-primary-400/40 ${
                isPastYear
                  ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                  : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:border-primary-400'
              }`}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <ChevronDownIcon className={`pointer-events-none absolute right-2.5 h-3.5 w-3.5 md:h-4 md:w-4 ${isPastYear ? 'text-amber-500' : 'text-gray-400'}`} />
          </div>

          {[
            { type: 'all', label: 'Alle', count: filterCounts.all, icon: UserGroupIcon },
            { type: 'members_only', label: 'Mitglieder', count: filterCounts.members, icon: UserIcon },
            { type: 'guests_only', label: 'Gäste', count: filterCounts.guests, icon: UserPlusIcon },
            { type: 'unpaid_current_year', label: `Unbezahlt ${selectedYear}`, count: filterCounts.unpaidCurrentYear, icon: CurrencyEuroIcon },
            { type: 'inactive', label: 'Inaktiv', count: filterCounts.inactive, icon: ExclamationCircleIcon },
            { type: 'cancelled', label: 'Gekündigt', count: filterCounts.cancelled, icon: CalendarDaysIcon },
            ...((isPastYear || showDeleted) && filterCounts.deleted > 0
              ? [{ type: 'deleted', label: 'Archiviert', count: filterCounts.deleted, icon: TrashIcon }]
              : []),
          ].map(({ type, label, count, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setFilterType(type as typeof filterType)}
              className={`inline-flex items-center gap-1.5 px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[11px] md:text-sm font-medium transition-all border ${
                filterType === type
                  ? 'bg-primary-400 border-primary-400 text-gray-900 dark:text-gray-900'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-400'
              }`}
            >
              <Icon className="h-3 w-3 md:h-3.5 md:w-3.5" />
              <span>{label}</span>
              <span className={`${filterType === type ? 'text-gray-700 dark:text-gray-800' : 'text-gray-400 dark:text-gray-500'}`}>({count})</span>
            </button>
          ))}

          {/* Gelöschte ein-/ausblenden (nur aktuelles Jahr; vergangene Jahre zeigen sie ohnehin) */}
          {!isPastYear && (
            <button
              onClick={() => {
                const turningOff = showDeleted;
                setShowDeleted(!showDeleted);
                if (turningOff && filterType === 'deleted') setFilterType('all');
              }}
              title="Ausgetretene und gelöschte Mitglieder ein-/ausblenden"
              className={`inline-flex items-center gap-1.5 px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[11px] md:text-sm font-medium transition-all border ${
                showDeleted
                  ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-amber-400'
              }`}
            >
              <TrashIcon className="h-3 w-3 md:h-3.5 md:w-3.5" />
              <span>{showDeleted ? 'Archiv ausblenden' : 'Archiv anzeigen'}</span>
            </button>
          )}
        </div>

        {/* Historik-Hinweis */}
        {isPastYear && (
          <div className="mb-3 md:mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs md:text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <ClockIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>Historische Ansicht: Mitgliederstand des Jahres <strong>{selectedYear}</strong> – inkl. der damals gelöschten Mitglieder (nur Lesezugriff).</span>
          </div>
        )}

      {/* Results Count - Compact on mobile */}
      <div className="mb-3 md:mb-4">
        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
          {filteredMembers.length === members.length ? (
            <span className="font-medium text-gray-700 dark:text-gray-300">{filteredMembers.length} Mitglieder</span>
          ) : (
            <span>
              <span className="font-medium text-primary-600 dark:text-primary-400">{filteredMembers.length}</span> von {members.length}
            </span>
          )}
        </p>
      </div>

      {/* Members List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Lade Mitglieder...</p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <UserGroupIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">Keine Mitglieder gefunden</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {searchTerm || filterType !== 'all'
              ? 'Versuchen Sie andere Suchbegriffe oder Filter'
              : 'Fügen Sie Ihr erstes Mitglied hinzu'}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile: Card View nach Mockup */}
          <div className="block lg:hidden space-y-2">
            {sortedMembers.map(member => {
              const dogStatus = dogsStatus[member.mg_nr];
              const payment = memberPayments[member.mg_nr];
              const noticeSummary = paymentNoticeSummaries[member.mg_nr];
              // Check if member is a guest (only has guest role)
              const isGuest = member.roles?.length === 1 && member.roles[0].name === 'guest';
              // Format member number: use mitglied_nr if available, otherwise mg_nr
              const displayNr = member.mitglied_nr || member.mg_nr;
              const memberNr = isGuest ? `#G-${displayNr}` : `#${displayNr}`;
              // Get guest cards count from API data
              const guestCardData = guestCards[member.mg_nr];
              const guestCardsCount = isGuest ? (guestCardData?.count || 0) : 0;
              const guestCardsRemaining = isGuest ? (guestCardData?.remaining || 0) : 0;

              // Get primary role info
              const primaryRole = [...(member.roles ?? [])].sort((a: any, b: any) => b.priority - a.priority)[0];
              let roleColorClass = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
              let roleLabel = '';
              if (member.active !== 1) {
                roleColorClass = 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300';
                roleLabel = 'INAKTIV';
              } else if (primaryRole) {
                switch(primaryRole.name) {
                  case 'board':
                    roleColorClass = 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300';
                    roleLabel = 'VORSTAND';
                    break;
                  case 'trainer':
                    roleColorClass = 'bg-info-100 dark:bg-info-900/30 text-info-800 dark:text-info-300';
                    roleLabel = 'TRAINER';
                    break;
                  case 'auditor':
                    roleColorClass = 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300';
                    roleLabel = 'PRÜFER';
                    break;
                  case 'member':
                    roleColorClass = 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300';
                    roleLabel = 'MITGLIED';
                    break;
                  case 'guest':
                    roleColorClass = 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
                    roleLabel = 'GAST';
                    break;
                  default:
                    roleLabel = primaryRole.description?.toUpperCase() || '';
                }
              }

              return (
                <div
                  key={member.mg_nr}
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors hover:border-primary-200 hover:bg-gray-50/40 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-700 dark:hover:bg-gray-800/80"
                  onClick={() => {
                    setSelectedMember(member);
                    setShowDetailsModal(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedMember(member);
                      setShowDetailsModal(true);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <MemberAvatar
                      member={member}
                      photoUrl={member.foto_path ? profileImages[member.mg_nr] : undefined}
                      hasImageError={imageErrors[member.mg_nr]}
                      onImageError={() => {
                        setImageErrors(prev => ({ ...prev, [member.mg_nr]: true }));
                      }}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <h3 className="min-w-0 truncate text-[15px] font-bold leading-5 text-gray-900 dark:text-white">
                          {member.vorname} {member.name}
                        </h3>
                        <HonoraryMembershipBadge member={member} />
                        {member.austritt_am && member.geloescht !== 1 && <span className="rounded-full bg-warning-100 px-2 py-0.5 text-xs font-semibold text-warning-800 dark:bg-warning-900/30 dark:text-warning-300">Gekündigt zum {member.austritt_am.split('-').reverse().join('.')}</span>}
                        {roleLabel && (
                          <span className={`flex-shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold tracking-wide ${roleColorClass}`}>
                            {roleLabel}
                          </span>
                        )}
                      </div>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {member.geloescht === 1 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            <TrashIcon className="h-2.5 w-2.5" />
                            {member.austritt_am ? 'Ausgetreten' : 'Gelöscht'}{member.geloescht_am ? ` ${new Date(member.geloescht_am).toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' })}` : ''}
                          </span>
                        )}
                        {!isGuest && (
                          <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
                            {memberNr}
                          </span>
                        )}

                        {isGuest && guestCardsCount > 0 && (
                          <span className="inline-flex min-w-0 items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                            <TicketIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{guestCardsCount} Karten · {guestCardsRemaining} übrig</span>
                          </span>
                        )}

                        {!isGuest && (() => {
                          const status = contributionStatus(payment, selectedYear, noticeSummary, member);
                          return (
                            <>
                              <span className={status.chipClass}>
                                {status.label}
                              </span>
                              {status.amount !== null && (
                                <span className={`text-[10px] ${status.amountClass}`}>
                                  {status.amount.toFixed(0)} €
                                </span>
                              )}
                            </>
                          );
                        })()}

                        {noticeSummary && (
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${noticeTone(noticeSummary.notice_type)}`}>
                            <EnvelopeIcon className="h-3 w-3" />
                            {noticeLabel(noticeSummary.notice_type)} · {new Date(noticeSummary.sent_at).toLocaleDateString('de-DE')}
                          </span>
                        )}

                        {dogStatus && dogStatus.count > 0 && (
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${
                            dogStatus.count === 1
                              ? 'text-gray-500 dark:text-gray-400'
                              : 'text-success-600 dark:text-success-400'
                          }`}>
                            <DogIcon className="h-3.5 w-3.5" />
                            {dogStatus.count}
                          </span>
                        )}

                        {member.verzehrliste === 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-400 dark:text-gray-500" title="Nicht auf der Verzehrliste">
                            <NoSymbolIcon className="h-3 w-3" />
                            Verzehr
                          </span>
                        )}

                        {(() => {
                          if (!member.last_login) {
                            return (
                              <span className="inline-flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                                <ClockIcon className="h-3 w-3" />
                                Nie
                              </span>
                            );
                          }

                          const lastLogin = new Date(member.last_login);
                          const now = new Date();
                          const diffDays = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24));
                          const colorClass = diffDays <= 7
                            ? 'text-success-600 dark:text-success-400'
                            : diffDays <= 30
                              ? 'text-warning-600 dark:text-warning-400'
                              : 'text-gray-400 dark:text-gray-500';
                          const label = diffDays === 0
                            ? 'Heute'
                            : diffDays <= 30
                              ? `${diffDays}d`
                              : `${Math.floor(diffDays / 30)}m`;

                          return (
                            <span className={`inline-flex items-center gap-1 text-[10px] ${colorClass}`}>
                              <ClockIcon className="h-3 w-3" />
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex flex-shrink-0 items-center gap-1.5">
                      {filterType === 'unpaid_current_year' && !isGuest && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setPaymentNoticeMember(member);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                          disabled={!member.email}
                          title={member.email ? 'Zahlungserinnerung oder Mahnung senden' : 'Keine E-Mail-Adresse hinterlegt'}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-35 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300"
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                        </button>
                      )}
                      <ChevronRightIcon className="h-[18px] w-[18px] text-gray-300 dark:text-gray-600" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>


          {/* Desktop: Table View - Variante 5 nach Mockup */}
          <div className="hidden lg:block bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700">
            {/* Header Row */}
            <div className="sticky top-16 z-10 grid grid-cols-[minmax(0,1fr)_320px_200px] gap-4 rounded-t-xl border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900/95 dark:backdrop-blur">
              <span className="text-[11px] font-bold uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400">
                Mitglied & Kontakt
              </span>
              <AttributeLabel iconKey="status" textClassName="text-[11px] font-bold uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400">
                Status
              </AttributeLabel>
              <AttributeLabel iconKey="balance" textClassName="text-[11px] font-bold uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400">
                Mitgliedsbeitrag
              </AttributeLabel>
            </div>

            {/* Data Rows */}
            {sortedMembers.map(member => {
              const dogStatus = dogsStatus[member.mg_nr];
              const payment = memberPayments[member.mg_nr];
              const noticeSummary = paymentNoticeSummaries[member.mg_nr];
              // Check if member is a guest (only has guest role)
              const isGuest = member.roles?.length === 1 && member.roles[0].name === 'guest';
              // Format member number: use mitglied_nr if available, otherwise mg_nr
              const displayNr = member.mitglied_nr || member.mg_nr;
              const memberNr = isGuest ? `#G-${displayNr}` : `#${displayNr}`;
              // Get guest cards count from API data
              const guestCardData = guestCards[member.mg_nr];
              const guestCardsCount = isGuest ? (guestCardData?.count || 0) : 0;
              const guestCardsRemaining = isGuest ? (guestCardData?.remaining || 0) : 0;
              const lastLoginBadge = (() => {
                if (!member.last_login) {
                  return (
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <ClockIcon className="h-3.5 w-3.5" />
                      Nie eingeloggt
                    </span>
                  );
                }
                const lastLogin = new Date(member.last_login);
                const now = new Date();
                const diffDays = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24));

                let colorClass = '';
                let label = '';

                if (diffDays === 0) {
                  colorClass = 'text-success-600 dark:text-success-400';
                  label = 'Heute';
                } else if (diffDays === 1) {
                  colorClass = 'text-success-600 dark:text-success-400';
                  label = 'Gestern';
                } else if (diffDays <= 7) {
                  colorClass = 'text-success-600 dark:text-success-400';
                  label = `vor ${diffDays} Tagen`;
                } else if (diffDays <= 30) {
                  colorClass = 'text-warning-600 dark:text-warning-400';
                  label = `vor ${diffDays} Tagen`;
                } else if (diffDays <= 90) {
                  colorClass = 'text-warning-600 dark:text-warning-400';
                  label = `vor ${Math.floor(diffDays / 30)} Mon.`;
                } else {
                  colorClass = 'text-gray-500 dark:text-gray-400';
                  label = lastLogin.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                }

                return (
                  <span className={`inline-flex items-center gap-1.5 text-xs whitespace-nowrap ${colorClass}`}>
                    <ClockIcon className="h-3.5 w-3.5" />
                    {label}
                  </span>
                );
              })();

              return (
                <div
                  key={member.mg_nr}
                  role="button"
                  tabIndex={0}
                  aria-label={`${member.vorname} ${member.name} öffnen`}
                  className="grid cursor-pointer grid-cols-[minmax(0,1fr)_320px_200px] items-center gap-4 border-b border-gray-100 px-4 py-2.5 transition-colors last:rounded-b-xl last:border-b-0 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:border-gray-700/50 dark:hover:bg-gray-700/50"
                  onClick={() => handleViewDetails(member)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleViewDetails(member);
                    }
                  }}
                >
                  {/* MITGLIED & KONTAKT: Avatar + Name + Nr + Email + Phone */}
                  <div className="flex min-w-0 items-center gap-3">
                    {/* Avatar mit Rollen-Badge */}
                    <MemberAvatar
                      member={member}
                      size="sm"
                      photoUrl={member.foto_path ? profileImages[member.mg_nr] : undefined}
                      hasImageError={imageErrors[`table-${member.mg_nr}`]}
                      onImageError={() => {
                        setImageErrors(prev => ({ ...prev, [`table-${member.mg_nr}`]: true }));
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                          {member.vorname} {member.name}
                        </p>
                        <HonoraryMembershipBadge member={member} />
                        {member.austritt_am && member.geloescht !== 1 && <span className="rounded-full bg-warning-100 px-2 py-0.5 text-xs font-semibold text-warning-800 dark:bg-warning-900/30 dark:text-warning-300">Gekündigt zum {member.austritt_am.split('-').reverse().join('.')}</span>}
                        {member.geloescht === 1 && (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            <TrashIcon className="h-3 w-3" />
                            {member.austritt_am ? 'Ausgetreten' : 'Gelöscht'}{member.geloescht_am ? ` ${new Date(member.geloescht_am).toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' })}` : ''}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex min-w-0 items-center gap-3">
                        {!isGuest && (
                          <span className="flex-shrink-0 text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
                            {memberNr}
                          </span>
                        )}
                        {member.email && (
                          <div className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                            <EnvelopeIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            <span className="truncate">{member.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* STATUS: feste Slots Rolle | Hunde | Login, damit Werte spaltenweise untereinander stehen */}
                  <div className="grid min-w-0 grid-cols-[124px_44px_minmax(0,1fr)] items-center gap-2">
                    {member.active === 1 ? (
                      member.roles && member.roles.length > 0 ? (
                        (() => {
                          const primaryRole = [...member.roles].sort((a: any, b: any) => b.priority - a.priority)[0];
                          let colorClass = '';
                          switch(primaryRole.name) {
                            case 'board':
                              colorClass = 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300';
                              break;
                            case 'trainer':
                              colorClass = 'bg-info-100 dark:bg-info-900/30 text-info-800 dark:text-info-300';
                              break;
                            case 'auditor':
                              colorClass = 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300';
                              break;
                            case 'member':
                              colorClass = 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300';
                              break;
                            case 'guest':
                              colorClass = 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
                              break;
                            default:
                              colorClass = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
                          }
                          return (
                            <span className={`inline-flex min-w-0 max-w-full items-center justify-self-start truncate rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${colorClass}`}>
                              {primaryRole.description}
                            </span>
                          );
                        })()
                      ) : <span />
                    ) : (
                      <span className="inline-flex items-center justify-self-start rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300">
                        Inaktiv
                      </span>
                    )}
                    {/* Dog Count - fester Slot, leer wenn 0; Farbe: 1=grau, 2+=grün */}
                    {dogStatus && dogStatus.count > 0 ? (
                      <span className={`inline-flex items-center justify-end gap-1 text-sm font-medium tabular-nums whitespace-nowrap ${
                        dogStatus.count === 1
                          ? 'text-gray-500 dark:text-gray-400'
                          : 'text-success-600 dark:text-success-400'
                      }`}>
                        <DogIcon className="h-4 w-4" />
                        {dogStatus.count}
                      </span>
                    ) : <span />}
                    {/* Login + optionales Verzehrlisten-Icon */}
                    <span className="inline-flex min-w-0 items-center gap-2">
                      {lastLoginBadge}
                      {member.verzehrliste === 0 && (
                        <NoSymbolIcon
                          className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500"
                          title="Nicht auf der Verzehrliste – wird beim Export ausgelassen"
                          aria-label="Nicht auf der Verzehrliste"
                        />
                      )}
                    </span>
                  </div>

                  {/* MITGLIEDSBEITRAG: Status for members, Cards for guests */}
                  <div className="min-w-0 space-y-1">
                    {isGuest ? (
                      /* Guests: Show guest cards count */
                      guestCardsCount > 0 ? (
                        <span className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                          <TicketIcon className="h-4 w-4" />
                          {guestCardsCount} Karten ({guestCardsRemaining} übrig)
                        </span>
                      ) : (
                        <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-900/20 dark:text-gray-400">
                          Keine Karten
                        </span>
                      )
                    ) : (
                      /* Members: Show status and optional amount */
                      (() => {
                        const status = contributionStatus(payment, selectedYear, noticeSummary, member);
                        return (
                          <>
                            <span className={status.chipClass}>
                              {status.label}
                            </span>
                            {status.amount !== null && (
                              <span className={`text-xs font-medium tabular-nums ${status.amountClass}`}>
                                {status.amount.toFixed(0)} €
                              </span>
                            )}
                          </>
                        );
                      })()
                    )}
                    {noticeSummary && (
                      <span className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${noticeTone(noticeSummary.notice_type)}`}>
                        <EnvelopeIcon className="h-3.5 w-3.5" />
                        {noticeLabel(noticeSummary.notice_type)}
                        <span className="font-normal opacity-75">{new Date(noticeSummary.sent_at).toLocaleDateString('de-DE')}</span>
                      </span>
                    )}
                    {filterType === 'unpaid_current_year' && !isGuest && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPaymentNoticeMember(member);
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                        disabled={!member.email}
                        title={member.email ? 'Zahlungserinnerung oder Mahnung senden' : 'Keine E-Mail-Adresse hinterlegt'}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-35 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50"
                      >
                        <PaperAirplaneIcon className="h-4 w-4" />
                        Beitragsmail
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      </div>

      {/* Member Detail Modal */}
      {showDetailsModal && selectedMember && (
        <MemberDetail
          member={selectedMember}
          profileImage={profileImages[selectedMember.mg_nr]}
          memberPayments={memberPayments[selectedMember.mg_nr]}
          paymentYear={selectedYear}
          dogsStatus={dogsStatus[selectedMember.mg_nr]}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedMember(null);
          }}
          onSave={isPastYear ? undefined : handleMemberSave}
          onDelete={isPastYear ? undefined : handleMemberDelete}
          onRestore={isPastYear ? undefined : handleMemberRestore}
        />
      )}

      {paymentNoticeMember && (
        <PaymentNoticeModal
          open
          member={paymentNoticeMember}
          year={selectedYear}
          rates={memberPayments[paymentNoticeMember.mg_nr]?.membership_contributions}
          summary={paymentNoticeSummaries[paymentNoticeMember.mg_nr]}
          onClose={() => setPaymentNoticeMember(null)}
          onSent={(summary) => {
            setPaymentNoticeSummaries(prev => ({ ...prev, [String(summary.member_id)]: summary }));
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'E-Mail versendet',
              message: `${noticeLabel(summary.notice_type)} wurde versendet und in der Historie protokolliert.`,
              variant: 'single',
              onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
              onCancel: () => {},
            });
          }}
        />
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: confirmModal.onCancel
          },
          confirm: {
            text: 'Bestätigen',
            onPress: confirmModal.onConfirm
          }
        } : undefined}
      />

      {/* CSV Importer Modal */}
      {showCSVImporter && (
        <MembersCSVImporter
          onImportSuccess={() => {
            setShowCSVImporter(false);
            fetchMembers();
          }}
          onClose={() => setShowCSVImporter(false)}
          availableRoles={availableRoles}
        />
      )}

      {/* Audit History Modal */}
      {showAuditModal && (
        <AuditHistoryModal onClose={() => setShowAuditModal(false)} />
      )}

      {/* Rollenverwaltung Modal */}
      {showRoleModal && (
        <RoleManagementModal
          onClose={() => setShowRoleModal(false)}
          onRolesChanged={() => {
            fetchAvailableRoles();
            fetchMembers();
          }}
        />
      )}

      {/* Excel Export Modal */}
      <MemberExcelExportModal
        isOpen={showExcelExportModal}
        onClose={() => setShowExcelExportModal(false)}
        isMobile={window.innerWidth < 768}
      />

      {/* Neues Mitglied Modal */}
      <NewMemberModal 
        isOpen={showNewMemberModal}
        onClose={() => {
          setShowNewMemberModal(false);
          setNewMemberData({
            vorname: '',
            name: '',
            email: '',
            tel_nummer: '',
            strasse: '',
            plz: '',
            ort: '',
            geb_am: '',
            gender: '',
            eintritt: '',
            active: 1,
            role_ids: [1] // Reset to default Guest role
          });
        }}
        data={newMemberData}
        setData={setNewMemberData}
        onSubmit={handleCreateMember}
        availableRoles={availableRoles}
      />
    </div>
  );
};

// Neues Mitglied Modal Component
const NewMemberModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  data: any;
  setData: (data: any) => void;
  onSubmit: () => void;
  availableRoles: any[];
}> = ({ isOpen, onClose, data, setData, onSubmit, availableRoles }) => {
  const [loading, setLoading] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit();
    setLoading(false);
  };

  return (
    <ModalShell
      open={isOpen}
      onClose={() => onClose()}
      variant="form"
      size="md"
      title="Neues Mitglied hinzufügen"
      subtitle="Persönliche Daten erfassen"
      icon={UserPlusIcon}
      closeOnBackdrop={false}
      footer={
        <ModalActions
          secondary={{ label: 'Abbrechen', onClick: () => onClose() }}
          primary={{
            label: loading ? 'Speichern...' : 'Mitglied hinzufügen',
            onClick: () => formRef.current?.requestSubmit(),
            disabled: loading || !data.vorname || !data.name,
            loading,
            icon: !loading ? <UserPlusIcon className="h-4 w-4" /> : undefined,
          }}
        />
      }
    >
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              {/* Persönliche Informationen - Card Style wie AddEditDogModal */}
              <ModalSection>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border-2 border-gray-300 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-primary-600" />
                  Persönliche Informationen
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <AttributeLabel iconKey="name" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Vorname *
                    </AttributeLabel>
                    <input
                      type="text"
                      name="vorname"
                      value={data.vorname}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Nachname *
                    </AttributeLabel>
                    <input
                      type="text"
                      name="name"
                      value={data.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="birthday" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Geburtsdatum
                    </AttributeLabel>
                    <input
                      type="date"
                      name="geb_am"
                      value={data.geb_am}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="gender" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Geschlecht
                    </AttributeLabel>
                    <select
                      name="gender"
                      value={data.gender ?? ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                    >
                      <option value="">⚪ Nicht angeben</option>
                      <option value="0">♂️ Männlich</option>
                      <option value="1">♀️ Weiblich</option>
                    </select>
                  </div>
                  <div>
                    <AttributeLabel iconKey="memberSince" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Eintrittsdatum {data.role_ids?.length === 1 && data.role_ids[0] === 1 &&
                        <span className="text-xs text-gray-500">(optional für Gäste)</span>}
                    </AttributeLabel>
                    <input
                      type="date"
                      name="eintritt"
                      value={data.eintritt || ''}
                      onChange={handleInputChange}
                      placeholder={data.role_ids?.length === 1 && data.role_ids[0] === 1 ? "Kein Eintrittsdatum für Gäste" : ""}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>
              </ModalSection>

              {/* Rollen und Berechtigungen - Card Style */}
              <ModalSection>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border-2 border-gray-300 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
                  Rolle auswählen
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableRoles.map((role) => {
                    const isChecked = data.role_ids?.includes(role.id) || false;
                    let iconClass = 'text-gray-400';
                    let bgClass = 'bg-gray-50 dark:bg-gray-700';
                    
                    if (isChecked) {
                      switch(role.name) {
                        case 'board':
                          iconClass = 'text-primary-600';
                          bgClass = 'bg-primary-50 dark:bg-primary-900/20 border-primary-300';
                          break;
                        case 'trainer':
                          iconClass = 'text-info-600';
                          bgClass = 'bg-info-50 dark:bg-info-900/20 border-info-300';
                          break;
                        case 'auditor':
                          iconClass = 'text-warning-600';
                          bgClass = 'bg-warning-50 dark:bg-warning-900/20 border-warning-300';
                          break;
                        case 'member':
                          iconClass = 'text-success-600';
                          bgClass = 'bg-success-50 dark:bg-success-900/20 border-success-300';
                          break;
                        case 'guest':
                          iconClass = 'text-gray-600 dark:text-gray-400';
                          bgClass = 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500';
                          break;
                      }
                    }
                    
                    return (
                      <label
                        key={role.id}
                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          bgClass
                        } ${
                          isChecked ? 'border-opacity-100' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const currentRoles = data.role_ids || [];
                            if (currentRoles.includes(role.id)) {
                              setData({
                                ...data,
                                role_ids: currentRoles.filter((id: number) => id !== role.id)
                              });
                            } else {
                              setData({
                                ...data,
                                role_ids: [...currentRoles, role.id]
                              });
                            }
                          }}
                          className="mr-3 rounded text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex items-center flex-1">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {role.description}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {role.name === 'guest' && '(Standard)'}
                              {role.name === 'member' && 'Vollmitglied'}
                              {role.name === 'trainer' && 'Trainer-Rechte'}
                              {role.name === 'board' && 'Vorstand'}
                              {role.name === 'auditor' && 'Kassenprüfer'}
                            </p>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {(!data.role_ids || data.role_ids.length === 0) && (
                  <p className="mt-2 text-sm text-warning-600 dark:text-warning-400">
                    ⚠️ Keine Rolle ausgewählt - Standard wird "Gast" sein
                  </p>
                )}
              </div>
              </ModalSection>

              {/* Kontaktinformationen - Card Style */}
              <ModalSection>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border-2 border-gray-300 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <EnvelopeIcon className="h-5 w-5 text-primary-600" />
                  Kontaktinformationen
                </h3>
                <div className="space-y-4">
                  <div>
                    <AttributeLabel iconKey="email" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      E-Mail
                    </AttributeLabel>
                    <input
                      type="email"
                      name="email"
                      value={data.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <AttributeLabel iconKey="phone" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Telefon
                    </AttributeLabel>
                    <input
                      type="tel"
                      name="tel_nummer"
                      value={data.tel_nummer}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
              </ModalSection>

              {/* Adresse - Card Style */}
              <ModalSection>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border-2 border-gray-300 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <MapPinIcon className="h-5 w-5 text-primary-600" />
                  Adresse
                </h3>
                <div className="space-y-4">
                  <div>
                    <AttributeLabel iconKey="street" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Straße
                    </AttributeLabel>
                    <input
                      type="text"
                      name="strasse"
                      value={data.strasse}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <AttributeLabel iconKey="address" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                        PLZ
                      </AttributeLabel>
                      <input
                        type="text"
                        name="plz"
                        value={data.plz}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <AttributeLabel iconKey="city" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Ort
                      </AttributeLabel>
                      <input
                        type="text"
                        name="ort"
                        value={data.ort}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
              </ModalSection>
      </form>
    </ModalShell>
  );
};

export default Members;
